import { FetchTransport, initializeFaro } from '@grafana/faro-web-sdk'
import { isNotFound, isRedirect } from '@tanstack/react-router'
import { FrontendErrorsInstrumentation } from './error-instrumentation'
import { sanitizeErrorItem } from './sanitize-error'
import type { Faro } from '@grafana/faro-web-sdk'
import type { RootOptions } from 'react-dom/client'
import type { ErrorInfo } from 'react'
import { env } from '@/env'
import i18n from '@/i18n'

let monitoring: Faro | undefined
const reportedErrors = new WeakSet<object>()

export function initializeMonitoring() {
  if (monitoring || !env.VITE_FARO_URL) return

  try {
    monitoring = initializeFaro({
      app: {
        name: 'amitable-web',
        environment: import.meta.env.MODE,
        version: __APP_COMMIT_HASH__,
        release: __APP_COMMIT_HASH__,
      },
      transports: [
        new FetchTransport({
          url: new URL(env.VITE_FARO_URL, window.location.origin).href,
          requestTimeoutMs: 2000,
          retry: { maxAttempts: 1 },
          requestOptions: { credentials: 'omit' },
        }),
      ],
      instrumentations: [
        new FrontendErrorsInstrumentation((error, source) =>
          reportFrontendError(error, source),
        ),
      ],
      sessionTracking: { enabled: false, persistent: false },
      trackGeolocation: false,
      preventGlobalExposure: true,
      dedupe: true,
      beforeSend: sanitizeErrorItem,
    })
  } catch {
    // Monitoring must never prevent the application from starting.
  }
}

export function reportFrontendError(
  error: unknown,
  source = 'manual',
  errorInfo?: Pick<ErrorInfo, 'componentStack'>,
) {
  if (
    !monitoring ||
    isNotFound(error) ||
    isRedirect(error) ||
    wasReported(error)
  )
    return
  monitoring.api.pushError(normalizeError(error), {
    context: {
      source,
      component_stack: errorInfo?.componentStack ?? '',
      language: i18n.resolvedLanguage ?? i18n.language,
      build: __APP_BUILD_NUMBER__,
    },
  })
}

function wasReported(error: unknown) {
  if (!error || typeof error !== 'object') return false
  if (reportedErrors.has(error)) return true
  reportedErrors.add(error)

  return false
}

function normalizeError(error: unknown) {
  if (error instanceof Error || error instanceof DOMException) return error

  return new Error(
    typeof error === 'string' ? error : 'Unexpected frontend error',
  )
}

export const monitoringRootOptions: RootOptions = {
  onCaughtError: (error, info) =>
    reportFrontendError(error, 'react.caught', info),
  onUncaughtError: (error, info) =>
    reportFrontendError(error, 'react.uncaught', info),
  onRecoverableError: (error, info) =>
    reportFrontendError(error, 'react.recoverable', info),
}
